import React, { useEffect, useMemo, useState } from 'react';
import type { SlaEntityType, SlaPolicyQuery, SlaPolicyResponse } from '@troia-v3/types';
import { toast } from 'sonner';

import {
  Button, DeleteConfirmationModal, FilterBottomSheet, FiltersButton,
  ListPageEmpty, ListPageLoading, PageToolbar, PlainHeadCell, SelectAllHeadCell, ToolbarSearch,
} from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { usePermissions } from '@/hooks/usePermissions';

import { SlaPolicyListItem } from '../components/SlaPolicyListItem';
import { SlaPolicyDrawer } from '../components/SlaPolicyDrawer';
import {
  useDeleteSlaPolicy, useReorderSlaPolicies, useSetDefaultSlaPolicy, useSlaPolicies,
} from '../hooks';
import { buildReorderScope, isDefaultLast, rankPoliciesForDisplay, reorderPolicyIds } from '../utils/slaPolicyHelpers';

/** Teto do `reorderSlaPoliciesSchema` (B2, `z.array(objectIdSchema).min(1).max(100)`). */
const REORDER_MAX_POLICIES = 100;

/**
 * SlaPoliciesPage — a cascata de políticas de SLA da empresa, na ordem em que
 * o motor as avalia.
 *
 * A ordem É a informação: `resolvePolicy` (Plano B) percorre as políticas
 * ativas por `order` crescente, DENTRO de um `entityType`, e a PRIMEIRA que
 * casa vence. Por isso não há coluna ordenável nem paginação nesta tela —
 * arrastar é a única ordenação.
 *
 * A ordem é dois níveis mais estreita do que a tabela sugere: (1) só políticas
 * ATIVAS são avaliadas — `SlaPoliciesService.reorder` rejeita com 400 qualquer
 * lista que não seja o conjunto completo de ativas retornado por
 * `findActiveForCompany`; (2) a cascata é POR `entityType` — misturar chamado
 * e conversa na mesma lista de reordenação também é 400 ("a lista mistura
 * ticket, conversation"). O arrasto abaixo (`handleDrop`) recorta o
 * subconjunto correto (ativas, mesmo `entityType` do item arrastado) antes de
 * montar o payload — a tabela continua mostrando TODAS as políticas (mesmo
 * inativas, com a pill "Inativa") porque esta é a tela de gestão delas.
 *
 * NÃO exibe relógio de entidade. Esta tela configura o contrato; quem mostra o
 * relógio é o card do chamado/conversa. Nenhum import de `sla.clocks`,
 * `slaBreached`, `slaBreachTime` ou `slaDueAt` pertence a este arquivo.
 */
export const SlaPoliciesPage: React.FC = () => {
  const { hasModulePermission } = usePermissions();
  const canRead = hasModulePermission('sla-policies', 'read');
  const canCreate = hasModulePermission('sla-policies', 'create');
  const canUpdate = hasModulePermission('sla-policies', 'update');
  const canDelete = hasModulePermission('sla-policies', 'delete');

  const [searchTerm, setSearchTerm] = useState('');
  const [entityFilter, setEntityFilter] = useState<SlaEntityType | undefined>(undefined);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<SlaPolicyResponse | null>(null);
  const [dragOrder, setDragOrder] = useState<string[] | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<SlaPolicyResponse | null>(null);

  // `limit: 100` = o teto do `reorderSlaPoliciesSchema` (B2). Ver a decisão 2
  // do brief desta tarefa: paginar a superfície de arrasto permitiria
  // reordenar a página 1 e renumerar em silêncio a página 2.
  const query: SlaPolicyQuery = useMemo(
    () => ({
      page: 1,
      limit: 100,
      ...(searchTerm ? { search: searchTerm } : {}),
      ...(entityFilter ? { filters: { entityType: entityFilter } } : {}),
    }),
    [searchTerm, entityFilter],
  );

  const { data, isLoading, error } = useSlaPolicies(canRead ? query : undefined);
  const deletePolicy = useDeleteSlaPolicy();
  const reorderPolicies = useReorderSlaPolicies();
  const setDefaultPolicy = useSetDefaultSlaPolicy();

  const items = useMemo(() => data?.items ?? [], [data]);

  // >100 políticas ATIVAS de um `entityType`: o `limit:100` da query já
  // trunca `items` abaixo do `data.total` real, então NENHUMA reordenação
  // computada a partir de `items` seria o conjunto completo que o backend
  // exige — mandaria sempre 400 "Faltaram: ...". Achado da Rodada 1 de
  // revisão: desabilitar o arrasto (com aviso) em vez de deixar o usuário
  // levar o erro ruidoso.
  const truncated = (data?.total ?? 0) > items.length;

  /** Ordem exibida: a local do arrasto enquanto a requisição não volta. */
  const orderedItems = useMemo(() => {
    if (!dragOrder) return items;
    const byId = new Map(items.map((p) => [p.id, p]));
    const ordered = dragOrder.map((id) => byId.get(id)).filter((p): p is SlaPolicyResponse => !!p);
    // Item que apareceu depois do arrasto (outra aba criou, ou ficou fora do
    // subconjunto reordenado — inativo/outro entityType) não some da tela.
    const missing = items.filter((p) => !dragOrder.includes(p.id));
    return [...ordered, ...missing];
  }, [items, dragOrder]);

  /**
   * Busca ativa = a listagem veio FILTRADA do servidor (`search` vai na query),
   * então `orderedItems` é um subconjunto da cascata. Também é o motivo de o
   * arrasto ficar bloqueado durante a busca (ver `draggable` na tabela).
   */
  const searchActive = searchTerm.trim() !== '';

  // Posição de AVALIAÇÃO por política, DENTRO do grupo (mesmo entityType, só
  // ativas) — ver `rankPoliciesForDisplay`. Achado da Rodada 1: numerar pelo
  // índice bruto do array exibido mentia sobre a cascata quando havia
  // inativa/outro tipo misturados. Achado da revisão final ampla (15/08): com
  // BUSCA ativa a numeração mente pelo mesmo mecanismo — `rankPoliciesForDisplay`
  // conta a sequência de ENTRADA, e a entrada é o resultado da busca. A tela
  // prefere não numerar ("—" + tooltip) a numerar errado.
  const ranks = useMemo(
    () => (searchActive ? new Map<string, number>() : rankPoliciesForDisplay(orderedItems)),
    [orderedItems, searchActive],
  );

  // A lista voltou do servidor já reordenada — a ordem local cumpriu o papel.
  useEffect(() => {
    if (!reorderPolicies.isPending) setDragOrder(null);
  }, [items, reorderPolicies.isPending]);

  const allSelected = items.length > 0 && items.every((p) => selectedIds.has(p.id));
  const someSelected = items.some((p) => selectedIds.has(p.id));

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSelectAll = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) items.forEach((p) => next.delete(p.id));
      else items.forEach((p) => next.add(p.id));
      return next;
    });

  const handleDragStart = (e: React.DragEvent<HTMLTableRowElement>, id: string) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  };

  const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent<HTMLTableRowElement>, targetId: string) => {
    e.preventDefault();
    // Defesa em profundidade: a linha nem fica `draggable` quando truncada
    // (ver a tabela abaixo), mas o alvo do drop pode ser qualquer `<tr>`.
    if (truncated) return;

    const draggedId = e.dataTransfer.getData('text/plain');
    const draggedPolicy = items.find((p) => p.id === draggedId);
    const targetPolicy = items.find((p) => p.id === targetId);
    if (!draggedPolicy || !targetPolicy) return;

    // As duas condições que o backend exige do payload de reordenação
    // (`SlaPoliciesService.reorder`, B3): mesmo `entityType` — a `order` de
    // conversa não interage com a de chamado — e só políticas ATIVAS — o
    // universo é `findActiveForCompany`, e uma inativa "não pertence ao
    // conjunto". Arrastar sobre uma linha que viola qualquer uma das duas é
    // no-op silencioso (a linha nem fica `draggable` — ver a página abaixo —
    // mas o alvo do drop pode ser qualquer `<tr>`).
    if (draggedPolicy.entityType !== targetPolicy.entityType) return;
    if (draggedPolicy.status !== 'active' || targetPolicy.status !== 'active') return;

    const scope = buildReorderScope(orderedItems, draggedPolicy.entityType);
    const ordered = reorderPolicyIds(scope, draggedId, targetId);
    if (!ordered) return;

    // Guard ANTES da requisição: o servidor recusa com 409 (ConflictError) a
    // reordenação que tira a política padrão do fim (SlaPoliciesService.reorder,
    // B3). Mandar assim mesmo entregaria ao usuário um erro que a tela já
    // sabia prever.
    if (!isDefaultLast(scope, ordered)) {
      toast.error('A política padrão precisa continuar sendo a última avaliada.');
      return;
    }

    setDragOrder(ordered);
    reorderPolicies.mutate({ policyIds: ordered });
  };

  const handleNew = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const handleEdit = (policy: SlaPolicyResponse) => {
    setEditing(policy);
    setDrawerOpen(true);
  };

  const confirmDelete = () => {
    if (deleteTarget) deletePolicy.mutate(deleteTarget.id);
    setDeleteTarget(null);
  };

  const entityLabel = entityFilter === 'ticket' ? 'Chamados' : entityFilter === 'conversation' ? 'Conversas' : null;
  const activeFilterCount = entityLabel ? 1 : 0;

  const filterOptions = (
    <>
      <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>
        Aplica-se a
      </p>
      {([[undefined, 'Todas'], ['ticket', 'Chamados'], ['conversation', 'Conversas']] as const).map(([val, label]) => {
        const active = entityFilter === val;
        return (
          <button
            key={label}
            onClick={() => { setEntityFilter(val); setFiltersOpen(false); }}
            className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
            style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
            data-testid={`sla-policies-filter-entity-${label}`}
          >
            {label}
            {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
          </button>
        );
      })}
    </>
  );

  if (!canRead) {
    return (
      <div className="h-full flex items-center justify-center px-6" data-testid="sla-policies-denied">
        <ListPageEmpty
          icon="clock"
          message="Sem acesso a Políticas de SLA"
          description="Peça a um administrador a permissão de leitura do módulo Políticas de SLA."
        />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      <PageToolbar
        left={<>
          <span className="font-bold text-xl tracking-tight truncate" style={{ color: 'var(--theme-text)' }}>
            Políticas de SLA
          </span>
          {data?.total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
              data-testid="sla-policies-count"
            >
              {data.total}
            </span>
          )}
        </>}
        center={
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Buscar política…"
            data-testid="sla-policies-search"
          />
        }
        right={<>
          <div className="relative flex-none">
            <FiltersButton
              count={activeFilterCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="sla-policies-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-60 rounded-xl p-2"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                  data-testid="sla-policies-filters-popover"
                >
                  {filterOptions}
                </div>
              </>
            )}
          </div>

          <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />

          {/* Único primário da tela; some para quem não pode criar (C20). */}
          {canCreate && (
            <Button variant="primary" icon="plus" collapsibleLabel onClick={handleNew} data-testid="new-sla-policy">
              Nova política
            </Button>
          )}
        </>}
        mobile={
          /* Busca + gatilho de Filtros, como nas páginas vizinhas: abaixo de
             `sm` a grade desktop (onde vive o `FiltersButton` do slot `right`)
             fica `hidden`, então sem este botão o `FilterBottomSheet` no fim do
             arquivo ficaria sem NENHUM gatilho — filtro inalcançável no mobile
             (achado da revisão final ampla, 15/08). O mesmo `filtersOpen`
             governa o popover (desktop) e o bottom sheet (mobile). */
          <>
            <ToolbarSearch
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Buscar política…"
              data-testid="sla-policies-search-mobile"
            />
            <FiltersButton
              count={activeFilterCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="sla-policies-filters-mobile"
            />
          </>
        }
      />

      {activeFilterCount > 0 && (
        <div
          className="flex-none flex items-center flex-wrap gap-2 px-4 py-2"
          style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}
        >
          <span
            className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold"
            style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
          >
            Aplica-se a: {entityLabel}
            <button
              onClick={() => setEntityFilter(undefined)}
              className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100"
              aria-label="Remover filtro de entidade"
            >
              <Icon name="x" className="w-3 h-3" />
            </button>
          </span>
          <button
            onClick={() => setEntityFilter(undefined)}
            className="text-xs font-semibold"
            style={{ color: 'var(--theme-muted-text)' }}
            data-testid="sla-policies-clear-filters"
          >
            Limpar tudo
          </button>
        </div>
      )}

      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div
              className="rounded-lg p-4"
              style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}
              data-testid="sla-policies-error"
            >
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar políticas: {error.message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading message="Carregando políticas..." />}

          {!isLoading && !error && items.length === 0 && (
            <ListPageEmpty
              icon="clock"
              message="Nenhuma política de SLA"
              description="A política padrão é semeada no primeiro boot. Crie uma política específica para diferenciar prazos por cliente, funil ou canal."
              {...(canCreate ? { actionLabel: 'Nova política', onAction: handleNew } : {})}
            />
          )}

          {!isLoading && !error && items.length > 0 && (
            <>
              <p className="text-xs mb-3" style={{ color: 'var(--theme-muted-text)' }}>
                O motor avalia de cima para baixo, dentro de cada tipo (chamados e conversas têm
                cascatas independentes), e para na primeira política ATIVA que casar. Arraste para
                mudar a ordem; a política padrão é sempre a última.
              </p>

              {truncated && (
                <p
                  className="text-xs mb-3 rounded-lg px-3 py-2"
                  style={{ color: 'var(--theme-warning-fg)', backgroundColor: 'var(--theme-warning-subtle)' }}
                  data-testid="sla-policies-truncated-warning"
                >
                  Esta empresa tem mais de {REORDER_MAX_POLICIES} políticas — o arrasto foi
                  desativado porque a reordenação precisa enviar o conjunto completo de uma vez
                  (teto do servidor). Desative ou exclua políticas para reordenar.
                </p>
              )}
              <div
                className="rounded-xl overflow-hidden"
                style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}
              >
                <table className="w-full border-collapse" data-testid="sla-policies-table">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <SelectAllHeadCell
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onToggle={toggleSelectAll}
                        ariaLabel="Selecionar todas"
                        data-testid="sla-policies-select-all"
                      />
                      {/* Sem SortableHeadCell: a ordem é o arrasto (decisão 1). */}
                      <PlainHeadCell className="w-10" />
                      <PlainHeadCell label="#" className="w-12" />
                      <PlainHeadCell label="Política" />
                      <PlainHeadCell label="Aplica-se a" />
                      <PlainHeadCell label="Status" />
                      <PlainHeadCell className="w-14" />
                    </tr>
                  </thead>
                  <tbody>
                    {orderedItems.map((policy) => (
                      <SlaPolicyListItem
                        key={policy.id}
                        policy={policy}
                        position={ranks.get(policy.id) ?? 0}
                        selected={selectedIds.has(policy.id)}
                        // Só é arrastável quem participa DE FATO da reordenação enviada
                        // ao servidor: ativa, não-padrão, lista sem termo de busca
                        // (busca filtra `items` no SERVIDOR — arrastar sobre um
                        // subconjunto pesquisado mandaria uma lista parcial, e o
                        // backend recusa com 400 "faltaram: ..."), e sem truncamento
                        // por >100 políticas (a mesma razão, teto do Zod).
                        draggable={
                          canUpdate && !policy.isDefault && policy.status === 'active'
                          && !searchActive && !truncated
                        }
                        canUpdate={canUpdate}
                        canDelete={canDelete}
                        // Com busca ativa nenhuma linha tem número (ver `ranks`):
                        // o "—" precisa dizer por quê, senão parece política
                        // inativa.
                        {...(searchActive
                          ? { positionHiddenReason: 'A ordem fica oculta durante a busca — a lista mostra só o resultado filtrado.' }
                          : {})}
                        dragDisabledReason={
                          truncated
                            ? `Mais de ${REORDER_MAX_POLICIES} políticas ativas — reduza para reordenar.`
                            : undefined
                        }
                        onToggleSelect={toggleSelect}
                        onEdit={handleEdit}
                        onDelete={setDeleteTarget}
                        onSetDefault={(p) => setDefaultPolicy.mutate(p.id)}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <FilterBottomSheet isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} data-testid="sla-policies-filters-sheet">
        {filterOptions}
      </FilterBottomSheet>

      <SlaPolicyDrawer
        isOpen={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditing(null); }}
        policy={editing}
        policies={orderedItems}
        ranksHidden={searchActive}
        onSelectPolicy={setEditing}
      />

      <DeleteConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Excluir política"
        message={`Tem certeza que deseja excluir a política "${deleteTarget?.name}"?`}
        warningMessage="Relógios já abertos por esta política continuam com o alvo que receberam — a exclusão vale para os próximos. Esta ação não pode ser desfeita."
        isLoading={deletePolicy.isPending}
        confirmButtonText="Excluir política"
      />
    </div>
  );
};

SlaPoliciesPage.displayName = 'SlaPoliciesPage';
